'use client'

import { useImageProvider } from '@7f-ui'
import { LoadingButton } from '@7f-ui/components/button/loading-button'
import { TextField } from '@7f-ui/components/text-field/text-field'
import { useToast } from '@7f-ui/components/toast/toast-provider'
import { NewsletterSignupDocument } from '@data-access/lib/gql/footer/documents'
import { useGraphQLMutation } from '@data-access/lib/graphql-query'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { EnvelopeSimple } from '@phosphor-icons/react'

import { shopTestId } from '@utils/utils-shop-test-ids'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useForm } from 'react-hook-form'
import * as v from 'valibot'

type FormData = {
  email: string
  firstName: string
}

export function NewsletterSignup() {
  const toast = useToast()

  const images = useImageProvider()

  const { mutate, isPending } = useGraphQLMutation(NewsletterSignupDocument)

  const validationSchema = v.object({
    email: v.pipe(
      v.string('Bitte gib eine gültige E-Mail-Adresse ein'),
      v.email('Bitte gib eine gültige E-Mail-Adresse ein')
    ),
    firstName: v.string('Bitte gib deinen Vornamen ein'),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: valibotResolver(validationSchema),
    defaultValues: { email: '', firstName: '' },
  })

  const onSuccess = () => {
    toast({
      type: 'success',
      message: 'Du hast dich erfolgreich registriert!',
    })
    reset()
  }

  const onSubmit = ({ email, firstName }: FormData) => {
    mutate({ input: { email, firstName } }, { onSuccess })
  }

  const pathname = usePathname()
  if (
    pathname?.startsWith('/newsletter-anmeldung') ||
    pathname?.startsWith('/checkout') ||
    pathname?.startsWith('/login') ||
    pathname?.startsWith('/register') ||
    pathname?.startsWith('/profile/expired-password') ||
    pathname?.startsWith('/profile/reset-password') ||
    pathname?.startsWith('/profile/new-password')
  ) {
    return null
  }

  return (
    <div className="bg-primary p-6 sm:py-12" data-testid={shopTestId('NewsletterSignup.root')}>
      <div className="flex flex-col items-center justify-center pb-2 md:flex-row">
        <div className="relative mb-6 h-[94px] w-[135px] md:mr-16 md:mb-0 xl:mr-18 xlg:h-[94px] xlg:w-[195px]">
          <Image src={images.newsletter} alt="Newsletter" fill />
        </div>

        <div className="flex flex-col items-center md:items-start">
          <h3
            className="pb-2 font-heading3 text-white"
            data-testid={shopTestId('NewsletterSignup.title')}
          >
            Der Freitag hat was.
          </h3>

          <p className="w-full pb-6 text-center font-body-copy text-white sm:w-[90%] md:w-full md:text-left">
            <span className="mr-1 xlg:mr-0 xlg:block">
              Unser Newsletter auch. Erlebnisse, neue Blickwinkel und Inspiration aus der Region –
              direkt in dein Postfach.
            </span>
          </p>

          <div className="w-full md:w-auto" data-testid={shopTestId('NewsletterSignup.form')}>
            <form
              className="flex flex-col items-center justify-center gap-6 sm:flex-row"
              onSubmit={handleSubmit(onSubmit)}
              id="newsletter-signup-form"
              noValidate
            >
              <TextField
                type="text"
                label="Dein Vorname"
                className="w-full sm:w-auto"
                {...register('firstName')}
                aria-invalid={errors.firstName ? 'true' : 'false'}
                error={errors.firstName?.message}
              />
              <TextField
                type="email"
                label="Deine E-Mail Adresse"
                IconStart={EnvelopeSimple}
                className="w-full min-w-80 sm:w-auto"
                {...register('email')}
                aria-invalid={errors.email ? 'true' : 'false'}
                error={errors.email?.message}
              />

              <LoadingButton
                isLoading={isPending}
                size="medium"
                type="submit"
                classNames={{ root: 'w-full sm:w-auto sm:!h-[50px]' }}
              >
                Jetzt anmelden
              </LoadingButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
